import React, { FC, useCallback, useContext, useMemo, useState } from 'react';

import { differenceInMinutes, parseISO } from 'date-fns';
import { FormikProps } from 'formik';

// helpers
import BackButton from '../../components/buttons/backButton';
// components
import BasicButton from '../../components/buttons/basicButton';
import FormulaStat from '../../components/formulas/FormulaStat';
import AampeSavingInfoIndicator from '../../components/shared/AampeSavingInfoIndicator/AampeSavingInfoIndicator';
import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';
import {
    CONTROLLED_FORMULA_BUFFER,
    PROCEED_SENDING,
} from '../../constants/formula';
import { FormulaType } from '../../constants/formulaType';

// context
import { EditorContext } from '../../context/EditorContext';

import { validateOverrideChannelContent } from '../../helpers/channelDefinitionHelpers';
import {
    disableWhenRTTrigger,
    getEditFormulaFormValues,
    getFormulaContentValidation,
    getFormulaStartEndDate,
} from '../../helpers/formulaHelpers';

// hoc
import { withFeatureFlags } from '../../hoc/withFeatureFlags';

// hooks
import useReachableUsers from '../../hooks/audiences/useReachableUsers';
import useFormulaDraft from '../../hooks/formulas/useFormulaDraft';

// models
import {
    FEATURE_FLAGS,
    WithFeatureFlagProps,
} from '../../models/feature-flag-config.model';
import {
    ApprovedFormulaState,
    ContentCardType,
    FormulaDetailModel,
    FormulaStartStopType,
    FormulaStatus,
    FormulaStatusData,
    FormulaTypesData,
    FormulaViewSetupFormValue,
} from '../../models/formula.model';
import { AampeStepperStepProps } from '../../models/stepper.model';

import EntityPermissionMain from 'views/entity-permission/EntityPermissionMain';

import FormulaEditModal from '../formulaView/FormulaEditModal';
import FormulaEditViewActions from '../formulaView/FormulaEditViewActions';
import FormulaStartTimeEdit from '../formulaView/FormulaStartTimeEdit';
import FormulaStatusView from '../formulaView/FormulaStatusView';
import FormulaViewActions from '../formulaView/FormulaViewActions';
import FormulaActionDropdown from './FormulaActionDropdown';
import FormulaNameTooltip from './FormulaNameTooltip';
import FormulaApproveAction from './formulaAction/FormulaApproveAction';
import FormulaReviewAction from './formulaAction/FormulaReviewAction';
import FormulaSendTestMessageAction from './formulaAction/FormulaSendTestMessageAction';

interface FormulaInfoComponentProps extends WithFeatureFlagProps {
    isUpdating?: boolean;
    sampleName?: string;
    setIsEditing?: (isEditing: boolean) => void;
    isEditing?: boolean;
    formikProps?: FormikProps<FormulaViewSetupFormValue>;
    isFormSubmitting?: boolean;
    steps?: AampeStepperStepProps[];
    backLink?: string;
    backText?: string;
    hasWriteAccess: boolean;
    hasCreateFormulaAccess: boolean;
    hasDeleteAccess: boolean;
}

const FormulaInfoComponent: FC<
    React.PropsWithChildren<FormulaInfoComponentProps>
> = ({
    isUpdating,
    sampleName,
    setIsEditing,
    isEditing,
    formikProps,
    isFormSubmitting,
    steps,
    backLink,
    backText = 'Back',
    featureFlags,
    hasCreateFormulaAccess,
    hasWriteAccess,
    hasDeleteAccess,
}) => {
    const {
        editorDetails: {
            isPreviewError,
            formula,
            isControlledFormula,
            previews,
            formulaType,
            isOverrideChannelContent,
            channelDefinition,
        },
    } = useContext(EditorContext);
    const draftFormula = useFormulaDraft(formula?.id as number);

    const [editStartTime, setEditStartTime] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

    const openEditModalHandler = () => setIsEditModalOpen(true);

    const closeEditModalHandler = () => setIsEditModalOpen(false);

    const editLiveMessageHandler = useCallback(() => {
        draftFormula.mutate({ is_in_edit_mode: true });
        closeEditModalHandler();
    }, [draftFormula]);

    const isUpdateStartTimeRequired = useCallback(() => {
        const {
            isCMSTypeChanged,
            isAudienceChanged,
            isCompoundTriggerChanged,
            isCmsFilterChanged,
            isStartTimeChanged,
        } = getEditFormulaFormValues(
            (formikProps?.initialValues ?? {}) as any,
            (formikProps?.values ?? {}) as any,
        );

        const sendDateTime = formula!.send_start?.split('+')[0];

        if (
            sendDateTime &&
            formula?.active_state === ApprovedFormulaState.Scheduled &&
            formula.is_controlled_message &&
            (isCMSTypeChanged ||
                isAudienceChanged ||
                isCompoundTriggerChanged ||
                isCmsFilterChanged ||
                isStartTimeChanged)
        ) {
            return (
                differenceInMinutes(parseISO(sendDateTime), new Date()) <
                CONTROLLED_FORMULA_BUFFER
            );
        }
        return false;
    }, [formula, formikProps]);

    const { data, isLoading: isLoading } = useReachableUsers(
        formula as FormulaDetailModel,
    );

    const filteredAudienceCount = useMemo(
        () => data?.total_filtered_audience || 0,
        [data],
    );

    const isDraftFormula = useMemo(
        () => formula?.active_state === FormulaStatus.Draft,
        [formula],
    );

    const isEditAllowed = useMemo(
        () =>
            [
                ApprovedFormulaState.Live,
                ApprovedFormulaState.Scheduled,
            ].includes(formula?.active_state as ApprovedFormulaState),
        [formula],
    );

    const onSave = () => {
        if (!isUpdateStartTimeRequired()) {
            formikProps?.handleSubmit();
        } else {
            setEditStartTime(true);
        }
    };

    const onCancel = () => {
        setIsEditing?.(false);
        formikProps?.resetForm();
    };

    const isInEditModeValidated = useMemo<boolean>(
        () =>
            !steps?.every(
                (step) =>
                    (step.getCompletionStatus && step.getCompletionStatus()) ||
                    !step.getCompletionStatus,
            ),
        [steps],
    );

    const contentCardType = useMemo<ContentCardType | undefined>(() => {
        if (formulaType === FormulaType.contentCards) {
            return formula?.content_card_type || ContentCardType.TextOnly;
        }
    }, [formula, formulaType]);

    const isValidated = useMemo(() => {
        if (isOverrideChannelContent) {
            return !(
                !!formikProps?.values.name &&
                validateOverrideChannelContent(
                    channelDefinition,
                    formula?.override_channel_fields,
                )
            );
        }
        const sample = previews?.[0] as any;
        return !(
            !!formikProps?.values.name &&
            getFormulaContentValidation(
                sample,
                formulaType as FormulaType,
                contentCardType,
            )
        );
    }, [
        formikProps,
        previews,
        formulaType,
        contentCardType,
        isOverrideChannelContent,
        channelDefinition,
        formula?.override_channel_fields,
    ]);

    const isApprovedFormula = useMemo<boolean>(
        () => FormulaStatus.Approved === (formula?.status as FormulaStatus),
        [formula],
    );

    const formulaScheduleData = useMemo<FormulaStartStopType>(
        () => getFormulaStartEndDate(formula),
        [formula],
    );

    const disableAndToolTipMessage = useMemo<{
        isNotValidated: boolean;
        buttonTooltipText: string;
    }>(() => {
        if (!formula?.reward_function_id) {
            return {
                isNotValidated: true,
                buttonTooltipText: 'Select a reward function to continue.',
            };
        }
        return disableWhenRTTrigger(
            formula?.compound_trigger?.trigger_type,
            featureFlags?.hasFlag(FEATURE_FLAGS.ALLOW_RESPONSIVE_FORMULAS),
        );
    }, [formula, featureFlags]);

    return (
        <>
            {editStartTime && formula && formikProps && hasWriteAccess && (
                <FormulaStartTimeEdit
                    handleClose={() => setEditStartTime(false)}
                    formikProps={formikProps}
                />
            )}
            <Container>
                <FormulaNameActionChannel>
                    <FormulaActionNameContainer>
                        <BackArrowAndNameContainer>
                            {backLink && (
                                <BackButton
                                    destination={backLink}
                                    tooltip={backText}
                                />
                            )}
                            <FormulaNameTooltip
                                tooltipPlacement="right"
                                text={sampleName || formula?.name || 'Unnamed'}
                            >
                                <FormulaName data-testid="formula_name">
                                    {sampleName || formula?.name || 'Unnamed'}
                                </FormulaName>
                            </FormulaNameTooltip>
                        </BackArrowAndNameContainer>
                        <EntityUserWithActions>
                            {formula?.id && (
                                <EntityPermissionMain
                                    formulaId={formula?.id}
                                    hasWriteAccess={hasWriteAccess}
                                />
                            )}
                            {
                                <FormulaActionMainContainer>
                                    {formula?.is_in_edit_mode &&
                                        hasWriteAccess && (
                                            <>
                                                <FormulaReviewAction
                                                    formulaId={formula?.id?.toString()}
                                                />

                                                <FormulaApproveAction
                                                    buttonText={PROCEED_SENDING}
                                                    disabled={
                                                        isInEditModeValidated ||
                                                        disableAndToolTipMessage.isNotValidated
                                                    }
                                                    buttonTooltipText={
                                                        disableAndToolTipMessage.buttonTooltipText
                                                    }
                                                    isPrimary={false}
                                                />
                                            </>
                                        )}
                                    {!isDraftFormula &&
                                        !formula?.review_request_by_id &&
                                        formula &&
                                        hasWriteAccess && (
                                            <FormulaViewActions
                                                formulaId={
                                                    formula?.id as number
                                                }
                                                formula={
                                                    formula as FormulaDetailModel
                                                }
                                                isFormulaArchived={
                                                    !!formula?.archived_at
                                                }
                                            />
                                        )}
                                    {!isEditing &&
                                        formula &&
                                        (isDraftFormula ||
                                            formula?.review_request_by_id) &&
                                        !formula?.is_in_edit_mode && (
                                            <FormulaSendTestMessageAction
                                                formulaId={formula?.id.toString()}
                                            />
                                        )}
                                    {!isEditing &&
                                        isEditAllowed &&
                                        hasWriteAccess && (
                                            <BasicButton
                                                name="Edit"
                                                isPrimary={false}
                                                action={openEditModalHandler}
                                            />
                                        )}
                                    {isEditModalOpen && (
                                        <FormulaEditModal
                                            onClose={closeEditModalHandler}
                                            onEditLiveMessage={
                                                editLiveMessageHandler
                                            }
                                            onEditMetadata={() => {
                                                setIsEditing?.(true);
                                                closeEditModalHandler();
                                            }}
                                        />
                                    )}
                                    {isEditing && hasWriteAccess && (
                                        <FormulaEditViewActions
                                            onSave={onSave}
                                            onCancel={onCancel}
                                            isLoading={isFormSubmitting}
                                            disabled={isValidated}
                                        />
                                    )}
                                    {formula?.id &&
                                        !isEditing &&
                                        !formula?.is_in_edit_mode && (
                                            <FormulaActionDropdown
                                                approveButtonTooltipText={
                                                    disableAndToolTipMessage.buttonTooltipText
                                                }
                                                setIsEditing={setIsEditing}
                                                isApproveActionDisabledProp={
                                                    isValidated ||
                                                    disableAndToolTipMessage.isNotValidated
                                                }
                                                hasCreateFormulaAccess={
                                                    hasCreateFormulaAccess
                                                }
                                                hasDeleteAccess={
                                                    hasDeleteAccess
                                                }
                                                hasWriteAccess={hasWriteAccess}
                                            />
                                        )}
                                </FormulaActionMainContainer>
                            }
                        </EntityUserWithActions>
                    </FormulaActionNameContainer>
                    <FormulaChannelStatus>
                        <FormulaStatusChannel>
                            <ChannelName>
                                {
                                    FormulaTypesData[formulaType as FormulaType]
                                        ?.title
                                }
                            </ChannelName>
                            <FormulaStageContainer>
                                <StageDot />
                                <FormulaStage>
                                    {
                                        FormulaStatusData[
                                            formula?.status as FormulaStatus
                                        ]?.label
                                    }
                                </FormulaStage>
                            </FormulaStageContainer>
                        </FormulaStatusChannel>

                        {isApprovedFormula && (
                            <FormulaStatusView
                                activeState={
                                    formula?.active_state as FormulaStatus
                                }
                                isControlledFormula={
                                    isControlledFormula as boolean
                                }
                                startDate={
                                    formulaScheduleData.send_start as string
                                }
                                endDate={
                                    formulaScheduleData.send_stop as string
                                }
                                timezone={formula?.timezone}
                            />
                        )}
                        {isDraftFormula && (
                            <AampeSavingInfoIndicator
                                updatedAt={formula?.updated_at}
                                isUpdating={isUpdating}
                            />
                        )}
                    </FormulaChannelStatus>
                </FormulaNameActionChannel>

                <FormulaStatsContainer>
                    <FormulaStat
                        text="Reachable users"
                        isLoading={isLoading}
                        messageCount={formula?.id ? filteredAudienceCount : 0}
                        tooltipText="This is the total number of users eligible with these filters."
                        horizontalView={false}
                    />
                    <FormulaStat
                        text="Alternate messages"
                        messageCount={formula?.message_count ?? 0}
                        error={
                            isPreviewError ? 'Message incomplete' : undefined
                        }
                        tooltipText="This is the total number of unique sendable messages generated by the combination of all of this message’s individual variants."
                        horizontalView={false}
                    />
                </FormulaStatsContainer>
            </Container>
        </>
    );
};

const Container = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(6),
    flexDirection: 'column',
    backgroundColor: aampeTheme.colors.white,
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing4(6),
});

const FormulaName = styled.span({
    fontSize: aampeTheme.size.font.title,
    lineHeight: aampeTheme.size.lineHeight.tall,
    color: aampeTheme.colors.textAccessibleWG02,
    fontWeight: aampeTheme.weights.medium,
});

const FormulaActionNameContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const BackArrowAndNameContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const FormulaStatsContainer = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(12),
});

const FormulaActionMainContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});

const FormulaChannelStatus = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(6),
    alignItems: 'center',
});

const Text = styled.span({
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: aampeTheme.size.lineHeight.small,
});

const ChannelName = styled(Text)({
    color: aampeTheme.colors.textAccessibleWG02,
    fontWeight: aampeTheme.weights.light,
});

const FormulaStage = styled(Text)({
    color: aampeTheme.colors.blue900,
    fontWeight: aampeTheme.weights.light,
});

const FormulaStageContainer = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing(1),
    alignItems: 'center',
});

const FormulaNameActionChannel = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
});

const StageDot = styled.div({
    display: 'flex',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: aampeTheme.colors.solidWG01,
});

const FormulaStatusChannel = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});

const EntityUserWithActions = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(3),
    justifyContent: 'center',
    alignItems: 'center',
});

export default withFeatureFlags(FormulaInfoComponent);
